'use client'

import { Flag24, QuestionCircle24, TrashBin24 } from '@vinted/monochrome-icons'
import { Cell, Icon, Note } from '@vinted/web-ui'
import { useState } from 'react'
import { v4 as uuid } from 'uuid'

import { CancelTransactionAction } from '@marketplace-web/conversation-shipping/conversation-shipping-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import {
  useBrowserNavigation,
  navigateToPage,
} from '@marketplace-web/browser/browser-navigation-util'
import { setSessionStorageItem } from '@marketplace-web/browser/browser-storage-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { urlWithParams } from '@marketplace-web/browser/url-util'
import {
  OppositeUser,
  TransactionModel,
  AdditionalTransactionAction,
  ConversationActionModal,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { clickEvent, messagingClickEvent } from '@marketplace-web/messaging/tracking-data'
import { INBOX_URL } from '@marketplace-web/messaging/navigation-feature'
import { REPORT_SESSION_ID_KEY, AlertType } from '@marketplace-web/admin-alert/admin-alert-data'
import { UserBlockAction } from '@marketplace-web/conversation-safety/conversation-safety-feature'

import { AccessChannel } from '@marketplace-web/help-center/help-center-faq-entry-url-data'

import {
  HELP_FAQ_ENTRIES_URL,
  NEW_ADMIN_ALERT_URL,
  TRANSACTION_FAQ_ENTRY_URL,
} from '../../constants/routes'
import DeleteConversationModal from '../DeleteConversationModal'

type Props = {
  transaction?: TransactionModel | null
  conversationId?: number | string
  oppositeUser?: OppositeUser | null
  userId?: number
}

const ConversationActions = ({ transaction, conversationId, oppositeUser, userId }: Props) => {
  const translate = useTranslate('conversation.conversation_info')
  const { track } = useTracking()
  const { relativeUrl } = useBrowserNavigation()
  const { conversation } = useConversationContext()

  const isConversationDeletionRestricted = conversation?.isDeletionRestricted

  const [visibleModalName, setVisibleModalName] = useState<ConversationActionModal | null>(null)

  const closeModals = () => {
    setVisibleModalName(null)
  }

  const handleDeleteConversationClick = () => {
    setVisibleModalName(ConversationActionModal.DeleteConversation)
  }

  const handleDeleteConversation = () => {
    closeModals()

    navigateToPage(INBOX_URL)
  }

  const trackHelp = () => {
    if (!transaction) return

    const targetDetails = {
      transaction_id: transaction.id,
    }

    track(
      clickEvent({
        target: 'help',
        screen: 'order_details',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const handleReportClick = () => {
    setSessionStorageItem(REPORT_SESSION_ID_KEY, uuid())

    track(
      clickEvent({
        screen: 'message_list',
        target: 'report_conversation',
        targetDetails: String(conversationId),
      }),
    )

    track(
      messagingClickEvent({
        screen: 'conversation_details',
        target: 'report_conversation',
        userSide: conversation?.transaction?.userSide,
        conversationId: String(conversationId),
      }),
    )
  }

  const getHelpCenterUrl = () => {
    if (!transaction) {
      return urlWithParams(HELP_FAQ_ENTRIES_URL, {
        access_channel: AccessChannel.ConversationNoTx,
        ref_url: relativeUrl,
      })
    }

    return urlWithParams(TRANSACTION_FAQ_ENTRY_URL(transaction.id), {
      access_channel: AccessChannel.ConversationTx,
      ref_url: relativeUrl,
      related_only: false,
    })
  }

  const renderHelpAction = () => {
    return (
      <Cell
        title={translate('conversation_actions.help')}
        prefix={<Icon name={QuestionCircle24} color="primary-default" />}
        type="navigating"
        url={getHelpCenterUrl()}
        onClick={trackHelp}
      />
    )
  }

  const renderMemberReportAction = () => {
    const isDeleteConversationActionExist = transaction?.availableActions?.includes(
      AdditionalTransactionAction.DeleteConversation,
    )
    const isReportingRestrictedForUser = transaction && !isDeleteConversationActionExist

    if (
      isReportingRestrictedForUser ||
      !conversationId ||
      !oppositeUser ||
      oppositeUser.isSystem ||
      oppositeUser.isModerator
    )
      return null

    const reportUrl = urlWithParams(NEW_ADMIN_ALERT_URL, {
      ref_id: conversationId,
      ref_type: AlertType.Conversation,
      ref_url: relativeUrl,
    })

    return (
      <Cell
        title={translate('conversation_actions.report')}
        prefix={<Icon name={Flag24} color="warning-default" />}
        type="navigating"
        url={reportUrl}
        onClick={handleReportClick}
        testId="conversation-actions-report"
      />
    )
  }

  const renderDeleteConversationAction = () => {
    const isDeleteConversationActionExist = transaction?.availableActions?.includes(
      AdditionalTransactionAction.DeleteConversation,
    )

    if ((transaction && !isDeleteConversationActionExist) || !conversationId || !userId) return null

    // TODO: replace the `Cell` with `Button` so we don't have to manage `aria-disabled` and `tabIndex` manually
    return (
      <>
        <Cell
          title={translate('conversation_actions.delete')}
          prefix={<Icon name={TrashBin24} color="warning-default" />}
          type="navigating"
          onClick={handleDeleteConversationClick}
          testId="conversation-actions-delete"
          disabled={isConversationDeletionRestricted}
          tabIndex={isConversationDeletionRestricted ? -1 : 0}
          aria={{ 'aria-disabled': isConversationDeletionRestricted }}
        />
        {isConversationDeletionRestricted && (
          <Note text={translate('conversation_actions.appeal_delete_note')} />
        )}
        <DeleteConversationModal
          conversationId={conversationId}
          show={visibleModalName === ConversationActionModal.DeleteConversation}
          onConfirm={handleDeleteConversation}
          onCancel={closeModals}
        />
      </>
    )
  }

  return (
    <>
      {renderHelpAction()}
      <CancelTransactionAction
        conversation={conversation}
        userId={userId}
        visibleModalName={visibleModalName}
        onSetVisibleModalName={setVisibleModalName}
      />
      {renderMemberReportAction()}
      {renderDeleteConversationAction()}
      <UserBlockAction
        oppositeUser={oppositeUser}
        userId={userId}
        visibleModalName={visibleModalName}
        onSetVisibleModalName={setVisibleModalName}
      />
    </>
  )
}

export default ConversationActions
